import { useEffect, useState } from "react"
import styled from "styled-components"
import { Button, TextInput } from "@prosolve/common-components-ui"
import { FaPlus, FaSyncAlt } from "react-icons/fa"
import useDataJobs from "../../dataHooks/useDataJobs"
import useSnackbar from "../../hooks/useSnackbar"
import useMe from "../../hooks/useMe"
import ConfirmDeleteJobModal from "./ConfirmDeleteJobModal"
import ConfirmRunJobModal from "./ConfirmRunJobModal"
import JobFormModal from "./JobFormModal"
import JobHistoryModal from "./JobHistoryModal"
import JobTable, { MutedText } from "./JobTable"
import { getJobId, toCopiedJob } from "./jobUtils"
import TestTaskModalButton from "./TestTaskModalButton"

const Container = styled.div`
  padding: 20px;
  color: ${({ theme }) => theme.colors.text};
`

const ColumnGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const RowGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const Header = styled(RowGroup)`
  justify-content: space-between;
  flex-wrap: wrap;
`

const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
`

const Group = styled.div`
  background: ${({ theme }) => theme.backgrounds.pure};
  border: ${({ theme }) => theme.borders.main};
  border-radius: ${({ theme }) => theme.borderRadius.main};
  padding: 20px;
`

const PanelTitle = styled.div`
  font-size: 18px;
  font-weight: bold;
`

export default () => {
  const [search, setSearch] = useState('')
  const [formJob, setFormJob] = useState()
  const [historyJob, setHistoryJob] = useState()
  const [deleteJob, setDeleteJob] = useState()
  const [jobToRun, setJobToRun] = useState()

  const { newMessage } = useSnackbar()
  const { hasEdit } = useMe()

  const {
    isLoading,
    jobs,
    jobHistory,
    fetchJobs,
    fetchCreateJob,
    fetchUpdateJob,
    fetchToggleJob,
    fetchDeleteJob,
    fetchRunJob,
    fetchJobHistory
  } = useDataJobs()

  useEffect(() => {
    fetchJobs()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshHistory = async (job = historyJob) => {
    const jobId = getJobId(job)
    if (jobId) {
      await fetchJobHistory(jobId)
    }
  }

  const openHistory = async (job) => {
    setHistoryJob(job)
    await refreshHistory(job)
  }

  const openClone = (job) => {
    setFormJob(toCopiedJob(job, jobs))
  }

  const submitJob = async (payload) => {
    const jobId = getJobId(formJob)
    const savedJob = jobId
      ? await fetchUpdateJob(jobId, payload)
      : await fetchCreateJob(payload)

    if (savedJob) {
      newMessage({ message: jobId ? 'Job updated' : 'Job created', severity: 'success' })
      await fetchJobs()
    }

    return savedJob
  }

  const toggleJob = async (job) => {
    const updatedJob = await fetchToggleJob(getJobId(job), !job.enabled)

    if (updatedJob) {
      newMessage({ message: updatedJob.enabled ? 'Job enabled' : 'Job disabled', severity: 'success' })
      await fetchJobs()
    }
  }

  const confirmRunJob = async () => {
    const job = jobToRun
    const result = await fetchRunJob(getJobId(job))

    if (result) {
      newMessage({
        message: result.status === 'success' ? 'Job ran successfully' : 'Job run failed',
        severity: result.status === 'success' ? 'success' : 'failure'
      })
      setJobToRun()
      await fetchJobs()
      if (getJobId(historyJob) === getJobId(job)) {
        await refreshHistory(job)
      }
    }
  }

  const confirmDeleteJob = async () => {
    const result = await fetchDeleteJob(getJobId(deleteJob))

    if (result?.success) {
      newMessage({ message: 'Job deleted', severity: 'success' })
      setDeleteJob()
      if (getJobId(historyJob) === getJobId(deleteJob)) {
        setHistoryJob()
      }
      await fetchJobs()
    }
  }

  return (
    <Container>
      <ColumnGroup style={{ gap: '15px' }}>
        <Header>
          <ColumnGroup>
            <Title>Job Manager</Title>
            <MutedText>Create, schedule, run, and inspect backend jobs.</MutedText>
          </ColumnGroup>
          <RowGroup>
            <Button onClick={fetchJobs}>
              <RowGroup>
                <FaSyncAlt />
                Refresh
              </RowGroup>
            </Button>
            <TestTaskModalButton />
            {hasEdit &&
              <Button $colored={true} onClick={() => setFormJob({})}>
                <RowGroup>
                  <FaPlus />
                  New Job
                </RowGroup>
              </Button>
            }
          </RowGroup>
        </Header>

        <Group>
          <ColumnGroup>
            <RowGroup style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <PanelTitle>Jobs</PanelTitle>
              <TextInput
                value={search}
                placeholder='Search jobs'
                width='260px'
                onChange={setSearch}
              />
            </RowGroup>
            <JobTable
              jobs={jobs}
              search={search}
              isLoading={isLoading}
              onRun={setJobToRun}
              onHistory={openHistory}
              onEdit={setFormJob}
              onClone={openClone}
              onToggle={toggleJob}
              onDelete={setDeleteJob}
            />
          </ColumnGroup>
        </Group>
      </ColumnGroup>

      {formJob &&
        <JobFormModal
          title={formJob.isClone ? 'Clone Job' : undefined}
          job={formJob}
          onClose={() => setFormJob()}
          onSubmit={submitJob}
        />
      }

      {historyJob &&
        <JobHistoryModal
          job={historyJob}
          history={jobHistory}
          isLoading={isLoading}
          onRefresh={() => refreshHistory()}
          onClose={() => setHistoryJob()}
        />
      }

      {deleteJob &&
        <ConfirmDeleteJobModal
          job={deleteJob}
          onClose={() => setDeleteJob()}
          onConfirm={confirmDeleteJob}
        />
      }

      {jobToRun &&
        <ConfirmRunJobModal
          job={jobToRun}
          onClose={() => setJobToRun()}
          onConfirm={confirmRunJob}
        />
      }
    </Container>
  )
}
